// Lightweight fetch wrapper for the customer app.
//
// Why not axios/ky: §6.4 + Phase 5 brief don't pre-authorize an HTTP client.
// `fetch` with credentials: 'include' is exactly what we need to ship the
// crauto-auth cookie (set same-origin by the backend in prod via Front Door;
// in dev via Vite's /api/* proxy → http://localhost:5000 — see vite.config.ts).

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// Inside the Teams personal tab the session rides on the SameSite=None
// crauto-teams-auth cookie, so every unsafe request carries this header — the
// server refuses one without it (standing-agents D13 antiforgery).
let teamsTab = false;

/** Turn on the Teams-tab antiforgery header (called once by the /teams/tab shell). */
export function setTeamsTabMode(on: boolean): void {
  teamsTab = on;
}

export const TEAMS_ANTIFORGERY_HEADER = 'X-Crauto-Teams';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * When true the function never throws on non-2xx; instead the caller
   * inspects the returned Response. Used by Login to read 401 bodies.
   */
  raw?: boolean;
}

async function readJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Issue an authenticated request against the regional control plane API.
 * Same-origin in prod (Front Door); dev runs through Vite's /api/* proxy.
 */
export async function apiFetch<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(teamsTab && (options.method ?? 'GET') !== 'GET' ? { [TEAMS_ANTIFORGERY_HEADER]: '1' } : {}),
    },
    signal: options.signal,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(path, init);

  if (options.raw) {
    return response as unknown as T;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = await readJsonSafe(response);
  if (!response.ok) {
    const message =
      typeof body === 'object' && body && 'message' in (body as Record<string, unknown>)
        ? String((body as Record<string, unknown>).message)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, body);
  }
  return body as T;
}
